import { z } from 'zod';

import { REQUEST_ESSENCE_MAX_LENGTH } from '@crm/entities/deal';
import { DateHelpers, sumSubmitFormatter } from '@crm/shared/lib';

import type { TFunction } from 'i18next';

export const createAddNewDealSchema = (t: TFunction) => {
    const optionalDateSchema = z
        .string()
        .optional()
        .transform((value) => value?.trim() ?? '')
        .refine(
            (value) => value === '' || DateHelpers.parseDisplayDate(value) !== undefined,
            t('crm.common.validation.invalidDate')
        )
        .transform((value) => {
            if (!value) {
                return undefined;
            }

            return DateHelpers.formatApiDate(DateHelpers.parseDisplayDate(value)!);
        });
    return z.object({
        dealName: z
            .string()
            .trim()
            .max(255, t('crm.addNewDeal.validation.dealNameMaxLength', { max: 255 }))
            .optional(),
        customerName: z
            .string(t('crm.addNewDeal.validation.customerNameRequired'))
            .trim()
            .min(1, t('crm.addNewDeal.validation.customerNameRequired'))
            .max(255, t('crm.addNewDeal.validation.customerNameMaxLength', { max: 255 })),
        phone: z
            .string()
            .optional()
            .transform((value) => value?.trim() || undefined),
        source: z
            .string()
            .trim()
            .max(255, t('crm.addNewDeal.validation.sourceMaxLength', { max: 255 }))
            .optional(),
        objectType: z
            .string()
            .trim()
            .max(255, t('crm.addNewDeal.validation.objectTypeMaxLength', { max: 255 }))
            .optional(),
        budget: z
            .string()
            .trim()
            .max(15, t('crm.addNewDeal.validation.budgetMaxLength', { max: 15 }))
            .optional()
            .transform((value) => (value ? sumSubmitFormatter(value) : undefined)),
        areaSquare: z
            .string()
            .trim()
            .optional()
            .transform((value) => (value ? sumSubmitFormatter(value) : undefined)),
        receivedAt: optionalDateSchema,
        description: z
            .string()
            .trim()
            .max(
                REQUEST_ESSENCE_MAX_LENGTH,
                t('crm.addNewDeal.validation.descriptionMaxLength', {
                    max: REQUEST_ESSENCE_MAX_LENGTH,
                })
            )
            .optional(),
    });
};

export type TFormInput = z.input<ReturnType<typeof createAddNewDealSchema>>;
export type TFormState = z.output<ReturnType<typeof createAddNewDealSchema>>;

export type TCreateDealPayload = TFormState & { columnId?: number };
export type TCreateDealResponse = TCreateDealPayload & {
    id: number;
};
